import { api } from 'utils';

// Constantes replicadas do core (EnumerablePortal) — páginas, categorias e
// campos ACF usados pelo portal. Fonte: sta-core-portal (WordPressService,
// HomeService, InstitutionalService, ContentService, etc.).
export const CATEGORY_CONTENT = 11;      // "Tudo"
export const CATEGORY_EVENTS = 16;       // "Eventos"
export const CATEGORY_CONTENT_MENU = 55; // menu de seções da home
export const CATEGORY_CONTENT_DETAIL = 158;
export const CATEGORY_LOGO_DETAIL = 149;

export const POST_FIELDS = 'id,slug,title,content,excerpt,categories,tags,date,acf';
export const PAGE_FIELDS = 'id,title,content,excerpt,featured_media,acf';
export const TAG_FIELDS = 'id,name';
export const CAT_FIELDS = 'id,name,slug,count,parent';

let tagsCache = null;
let categoriesCache = null;
let colorsCache = null;

const getTagsAll = async () => {
    if (!tagsCache) {
        const { data } = await api.get('/wp/v2/tags', { params: { per_page: 100, _fields: TAG_FIELDS } });
        tagsCache = data;
    }
    return tagsCache;
};

const getCategoriesAll = async () => {
    if (!categoriesCache) {
        const { data } = await api.get('/wp/v2/categories', { params: { per_page: 100, _fields: CAT_FIELDS } });
        categoriesCache = data;
    }
    return categoriesCache;
};

// cor_categoria: o core buscava nos posts da categoria ContentDetail (158)
// qual post tinha EXATAMENTE a categoria "filha" + 158, e usava o campo ACF
// cor_categoria. Aqui mapeamos { categoryId: cor } na primeira ocorrência.
const getCategoryColors = async () => {
    if (!colorsCache) {
        const { data } = await api.get('/wp/v2/posts', {
            params: { categories: CATEGORY_CONTENT_DETAIL, per_page: 100, _fields: 'id,categories,acf' }
        });
        const map = {};
        data.forEach(post => {
            const real = post.categories.filter(c => c !== CATEGORY_CONTENT_DETAIL && c !== CATEGORY_CONTENT && c !== CATEGORY_CONTENT_MENU);
            if (real.length && !map[real[0]] && post.acf?.cor_categoria) {
                map[real[0]] = post.acf.cor_categoria;
            }
        });
        colorsCache = map;
    }
    return colorsCache;
};

// Categoria "de exibição" de um post: o core (SetContentMainCategory) usa as
// categorias que possuem post de detalhe (158) para escolher — preferimos a
// categoria com cor_categoria; fallback: primeira categoria "real" do post.
export const getDisplayCategory = (post, categories, colors = {}) => {
    const EXCLUDED = [CATEGORY_CONTENT, CATEGORY_CONTENT_MENU, CATEGORY_CONTENT_DETAIL, CATEGORY_LOGO_DETAIL];
    const cats = post.categories || [];
    const id = cats.find(c => !EXCLUDED.includes(c) && colors[c]);
    const fallbackId = cats.find(c => !EXCLUDED.includes(c));
    return categories.find(c => c.id === (id || fallbackId)) || null;
};

const mapTagList = async (tagIds) => {
    const all = await getTagsAll();
    return all.filter(t => tagIds.includes(t.id)).map(t => ({ id: t.id, name: t.name }));
};

// Converte um post da WP REST API no formato consumido pelas views
// (mesma forma dos DTOs do core: title, summary, category, color, media,
// imagemThumbnail, solutionURL, target, tagList, dateEvent, finalDateEvent).
export const mapPost = async (post, categories, colors, includeTags = true) => {
    const display = getDisplayCategory(post, categories, colors);
    const result = {
        id: post.id,
        slug: post.slug,
        title: post.title?.rendered || '',
        summary: post.excerpt?.rendered || '',
        content: post.content?.rendered || '',
        category: display?.name || '',
        color: display ? (colors[display.id] || '') : '',
        media: { url: post.acf?.imagem || '' },
        imagemThumbnail: post.acf?.imagem_thumbnail || null,
        solutionURL: post.acf?.url_solucoes || '',
        target: post.acf?.target || '',
        dateEvent: post.acf?.data_hora_do_evento || null,
        finalDateEvent: post.acf?.data_final_evento || null
    };
    if (includeTags) {
        result.tagList = post.tags ? await mapTagList(post.tags) : [];
    }
    return result;
};

// Converte uma página da WP REST API no formato das views (PageViewModel).
// image = featured_media (via _embed); logo = campo ACF "Logo" (string JSON,
// a view faz JSON.parse — mesmo comportamento do core).
export const mapPage = (page) => {
    if (!page) return null;
    const featured = page._embedded?.['wp:featuredmedia']?.[0]?.source_url || '';
    return {
        id: page.id,
        slug: page.slug,
        title: page.title?.rendered || '',
        content: page.content?.rendered || '',
        description: page.excerpt?.rendered || '',
        image: featured || (page.acf?.imagem_header && page.acf.imagem_header !== 'False' ? page.acf.imagem_header : '') || page.acf?.imagem || '',
        media: { url: page.acf?.imagem || '' },
        logo: page.acf?.Logo || null,
        subtitle: page.acf?.subtitulo || null,
        css: page.elementor_css || '',
        date: page.date || null
    };
};

// mainContentCategory: primeira categoria filha de "Conteúdos" (11) do post.
export const getMainContentCategory = async (postCategories) => {
    if (!postCategories || !postCategories.length) return null;
    const categories = await getCategoriesAll();
    const children = categories.filter(c => c.parent === CATEGORY_CONTENT);
    const found = children.find(c => postCategories.includes(c.id));
    return found ? found.id : null;
};

// Brands: mesmo critério do core (PostService.GetPostBySlug) — posts da
// categoria LogoDetail (149) que compartilham uma categoria com o post.
export const resolveBrands = async (postCategories) => {
    const result = {
        logoBrands: '', nameBrands: '', slugBrands: '', urlSolutions: '', urlSolutionsTarget: ''
    };
    if (!postCategories || !postCategories.length) return result;

    const { data: detailPosts } = await api.get('/wp/v2/posts', {
        params: { categories: CATEGORY_LOGO_DETAIL, per_page: 50, _fields: 'id,categories,acf' }
    });

    let idBrand = 0;
    detailPosts.forEach(d => {
        if (!idBrand) {
            const inter = (d.categories || []).find(c => postCategories.includes(c));
            if (inter) idBrand = inter;
        }
    });
    if (!idBrand) return result;

    const { data: brands } = await api.get('/wp/v2/posts', {
        params: { categories: [idBrand, CATEGORY_LOGO_DETAIL].join(','), per_page: 50, _fields: 'id,categories,acf' }
    });

    const item = brands.find(b => {
        const cats = b.categories || [];
        return cats.length === 2 && cats.includes(idBrand) && cats.includes(CATEGORY_LOGO_DETAIL);
    });

    if (item) {
        const url = item.acf?.url_solucoes || '';
        result.logoBrands = item.acf?.imagem || '';
        result.urlSolutions = url;
        result.slugBrands = url.split('/').filter(Boolean).pop() || '';
        result.nameBrands = (url.split('/').filter(Boolean).pop() || '').replace(/-/g, ' ');
    }

    return result;
};

// Converte um post em "detalhe" (ContentViewModel) para content.jsx.
export const mapPostDetail = async (post) => {
    const [mainContentCategory, brands] = await Promise.all([
        getMainContentCategory(post.categories),
        resolveBrands(post.categories)
    ]);

    const data = {
        id: post.id,
        slug: post.slug,
        title: post.title?.rendered || '',
        description: post.excerpt?.rendered || '',
        content: post.content?.rendered || '',
        date: post.date || null,
        mainContentCategory,
        media: { url: post.acf?.imagem || '' },
        imageHeader: post.acf?.imagem_header || null,
        imageMobile: post.acf?.imagem_mobile || '',
        imageDesktop: post.acf?.imagem_desktop || '',
        subtitle: post.acf?.subtitulo || null,
        author: post.acf?.autor || null,
        authorPhoto: post.acf?.foto_do_autor || '',
        fontSizeDesktop: post.acf?.font_size_desktop || null,
        fontSizeMobile: post.acf?.font_size_mobile || null,
        urlSolutions: brands.urlSolutions,
        urlSolutionsTarget: brands.urlSolutionsTarget || post.acf?.target || '',
        logo: null,
        brands: {
            logoBrands: brands.logoBrands,
            nameBrands: brands.nameBrands,
            slugBrands: brands.slugBrands
        },
        tagList: post.tags ? await mapTagList(post.tags) : [],
        tags: post.tags || []
    };

    if (post.acf?.data_hora_do_evento) {
        data.event = {
            eventDate: toISOwithSpace(post.acf.data_hora_do_evento),
            eventFinalDate: post.acf.data_final_evento ? toISOwithSpace(post.acf.data_final_evento) : null,
            eventPlace: post.acf.local_do_evento || null,
            subscriptionUrl: post.acf.link_inscricao || null
        };
    }

    return data;
};

// ACF guarda data como "dd/MM/yyyy HH:mm:ss"; as views esperam "yyyy-MM-dd HH:mm:ss"
const toISOwithSpace = (value) => {
    const m = /^(\d{2})\/(\d{2})\/(\d{4}) (\d{2}:\d{2}:\d{2})$/.exec(value);
    if (!m) return value;
    return `${m[3]}-${m[2]}-${m[1]} ${m[4]}`;
};

export const getContext = async () => {
    const [categories, colors] = await Promise.all([getCategoriesAll(), getCategoryColors()]);
    return { categories, colors };
};

export { getTagsAll, getCategoriesAll, getCategoryColors };
